<template>
  <div class="picture-card-image-uploader">
    <div
      class="upload-picture-card-wrap"
      :class="{ 'is-sm': size === 'sm', 'is-md': size === 'md' }"
    >
      <div
        v-for="(item, idx) in fileList"
        :key="idx"
        class="upload-item"
        :class="{ uploading: item.status === 'uploading' }"
      >
        <template v-if="item.status === 'uploading'">
          <div class="upload-item__placeholder">
            <i class="el-icon-loading" />
          </div>
        </template>
        <template v-else>
          <img :src="item.url" class="upload-item__img" alt="">
          <span class="upload-item__actions">
            <i
              v-if="showZoom"
              class="el-icon-zoom-in"
              @click="openPreview(item.url)"
            />
            <i class="el-icon-delete" @click="removeImage(idx)" />
          </span>
        </template>
      </div>
      <el-upload
        v-if="fileList.length < max"
        class="upload-trigger"
        :action="uploadAction"
        :headers="uploadHeaders"
        :show-file-list="false"
        :before-upload="handleBeforeUpload"
        :on-success="handleUploadSuccess"
        :on-error="handleUploadError"
        accept="image/*"
        :data="uploadData"
      >
        <div class="upload-trigger__btn">
          <i class="el-icon-plus" />
        </div>
      </el-upload>
    </div>
    <div v-if="showTip" class="upload-tip">
      {{ tip }}
    </div>
    <ImagePreviewDialog
      :visible.sync="previewVisible"
      :image-url="previewUrl"
      title="图片预览"
      width="600px"
    />
  </div>
</template>
<script>
import axios from 'axios';
import { uniqueFileIds } from './imageFileIdsUtil';
import ImagePreviewDialog from './ImagePreviewDialog.vue';

/**
 * 多图 picture-card 上传，v-model 为 fileId 数组（文件服务 initUpload + iff 图片接口预览）
 */
export default {
  name: 'ImageUploader',
  components: { ImagePreviewDialog },
  props: {
    value: { type: Array, default: () => [] },
    max: { type: Number, default: 5 },
    showZoom: { type: Boolean, default: false },
    showTip: { type: Boolean, default: true },
    tip: { type: String, default: '最多上传5张，支持 jpg/png 格式' },
    size: {
      type: String,
      default: 'sm',
      validator: v => ['sm', 'md'].includes(v)
    }
  },
  data () {
    return {
      fileList: [],
      uploadAction: this.$config.fileApi.upload,
      uploadData: { meta: [{ key: 'type', value: 'fileId' }] },
      previewVisible: false,
      previewUrl: ''
    };
  },
  computed: {
    uploadHeaders () {
      return { [this.$config.serverHeaderTokenName]: this.$HMCookies.get(this.$config.serverHeaderTokenName) };
    }
  },
  watch: {
    value: {
      immediate: true,
      deep: true,
      handler (newIds) {
        this.syncValueToFileList(newIds);
      }
    }
  },
  methods: {
    async syncValueToFileList (newIds) {
      if (this.fileList.some(i => i.status === 'uploading')) return;
      const ids = uniqueFileIds(Array.isArray(newIds) ? newIds : []);
      const currentDone = this.fileList.filter(i => i.status === 'done').map(i => i.fileId);
      if (ids.length === currentDone.length && ids.every((id, i) => id === currentDone[i])) return;
      if (!ids.length) {
        this.fileList = [];
        return;
      }
      const headers = this.uploadHeaders;
      const base = `${this.$config.ctx.iffApi}/common/image/`;
      const list = [];
      for (let i = 0; i < ids.length; i++) {
        const fileId = ids[i];
        try {
          const r = await axios.get(`${base}${fileId}`, { headers });
          const data = r.data && r.data.data ? r.data.data : '';
          list.push({
            status: 'done',
            fileId,
            url: data ? 'data:image/png;base64,' + data : ''
          });
        } catch (e) {
          list.push({ status: 'done', fileId, url: '' });
        }
      }
      this.fileList = list;
    },
    openPreview (url) {
      this.previewUrl = url;
      this.previewVisible = true;
    },
    async handleBeforeUpload (file) {
      const placeholder = { status: 'uploading', url: '', fileId: '' };
      this.fileList.push(placeholder);
      const idx = this.fileList.length - 1;
      this.uploadData.md5 = Date.now();
      this.uploadData.name = file.name;
      try {
        const res = await axios.post(this.$config.fileApi.initUpload, this.uploadData, { headers: this.uploadHeaders });
        this.uploadData.fileId = res.data.fileId;
        this.fileList[idx].fileId = res.data.fileId;
      } catch (e) {
        this.fileList.splice(idx, 1);
        return Promise.reject(new Error('init failed'));
      }
    },
    handleUploadSuccess (response) {
      const fileId = response.fileId || this.uploadData.fileId;
      const item = this.fileList.find(i => i.fileId === fileId && i.status === 'uploading');
      if (item) {
        item.status = 'done';
        const imageApi = this.$config.ctx.iffApi + '/common/image';
        axios.get(`${imageApi}/${fileId}`, { headers: this.uploadHeaders })
          .then(res => { item.url = 'data:image/png;base64,' + res.data.data; })
          .catch(() => { item.url = ''; });
        const doneIds = uniqueFileIds(this.fileList.filter(i => i.status === 'done').map(i => i.fileId));
        this.$emit('input', doneIds);
      }
    },
    handleUploadError () {
      const idx = this.fileList.findIndex(i => i.status === 'uploading');
      if (idx !== -1) this.fileList.splice(idx, 1);
    },
    removeImage (idx) {
      this.fileList.splice(idx, 1);
      this.$emit('input', uniqueFileIds(this.fileList.filter(i => i.status === 'done').map(i => i.fileId)));
    },
    isUploading () {
      return this.fileList.some(i => i.status === 'uploading');
    }
  }
};
</script>

<style lang="scss" scoped>
.upload-picture-card-wrap {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  &.is-sm .upload-item,
  &.is-sm .upload-trigger__btn {
    width: 70px;
    height: 70px;
  }
  &.is-md .upload-item,
  &.is-md .upload-trigger__btn {
    width: 80px;
    height: 80px;
  }
}

.upload-item {
  border-radius: 4px;
  border: 1px solid #c0ccda;
  position: relative;
  overflow: hidden;
  background: #fff;

  &.uploading {
    background: #f5f7fa;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .upload-item__placeholder {
    color: #909399;
    font-size: 20px;
  }

  .upload-item__img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }

  .upload-item__actions {
    position: absolute;
    inset: 0;
    background: rgba(0, 0, 0, 0.5);
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 12px;
    opacity: 0;
    transition: opacity 0.2s;
    cursor: pointer;
    i {
      color: #fff;
      font-size: 16px;
    }
  }

  &:hover .upload-item__actions {
    opacity: 1;
  }
}

.upload-trigger ::v-deep .el-upload {
  display: block;
}

.upload-trigger__btn {
  border: 1px dashed #d9d9d9;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: #8c939d;
  font-size: 20px;
  transition: border-color 0.2s;
  background: #fff;
  &:hover {
    border-color: #409eff;
    color: #409eff;
  }
}

.upload-tip {
  font-size: 12px;
  color: #909399;
  margin-top: 6px;
}
</style>